/**
 * The Odoo projects of the catalog, as the sidebar and the home page list
 * them — one read for both (see `sharedLoad.ts`).
 *
 * Queried with the caller's own credentials (the catalog API attaches them),
 * so the permission policy decides which projects come back: a non-admin sees
 * the projects whose `prj-<project>` group they belong to.
 */

import {
  CATALOG_FILTER_EXISTS,
  entityRouteParams,
  type CatalogApi,
} from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import {
  APP_TYPE_ANNOTATION,
  appTypeOf,
  PROJECT_ENTITY_FIELDS,
  PROJECT_LABEL,
  projectIdOf,
} from '../shared';
import {
  projectCreation,
  projectRepositoryUrl,
  type ProjectCreation,
} from './projectEntity';
import { createSharedLoad, type SharedLoadOptions } from './sharedLoad';

/**
 * One Odoo project, and where its catalog entity lives.
 *
 * `projectId` is the project id (`projectIdOf`: the `waadoo.ovh/project`
 * label) — the identity ArgoCD knows the project by — while `label` is what a
 * human called it. The
 * two are kept apart because the screens show one and query with the other.
 */
export interface ProjectEntry {
  /** React key. Two entities may carry the same annotation; refs are unique. */
  entityRef: string;
  projectId: string;
  label: string;
  params: ReturnType<typeof entityRouteParams>;
  /**
   * Its `AppType` (`appTypeOf`), whose descriptor designates the templates
   * its buttons launch (components/shared/templateRoles.ts).
   */
  appType: string;
  /** Who created it and when, when its template said so. */
  creation?: ProjectCreation;
  /** Its code repository, when the catalog says where it read it from. */
  repositoryUrl?: string;
}

/** Same TTL as the ArgoCD list, for the same reason (`projectApplications.ts`). */
const PROJECTS_TTL_MS = 30_000;

const sharedProjects = createSharedLoad<ProjectEntry[]>(PROJECTS_TTL_MS);

/**
 * The genuine project entry points, sorted by label.
 *
 * Not only a type marker: the entity must also carry the project label and be
 * owned by `prj-<project>`, or a forged entity would be listed under someone
 * else's project — see `components/shared/projectIdentity.ts`. The filter
 * narrows server-side on the label and the type annotation; `projectIdOf`
 * then checks the owner and that the type is not empty.
 */
export function loadProjectEntries(
  catalogApi: Pick<CatalogApi, 'getEntities'>,
  options?: SharedLoadOptions,
): Promise<ProjectEntry[]> {
  return sharedProjects.load(async () => {
    const { items } = await catalogApi.getEntities({
      filter: {
        [`metadata.labels.${PROJECT_LABEL}`]: CATALOG_FILTER_EXISTS,
        [`metadata.annotations.${APP_TYPE_ANNOTATION}`]: CATALOG_FILTER_EXISTS,
      },
      fields: [
        'kind',
        'metadata.namespace',
        'metadata.name',
        'metadata.title',
        ...PROJECT_ENTITY_FIELDS,
      ],
    });

    return items
      .flatMap(entity => {
        const projectId = projectIdOf(entity);
        // Both or neither: `appTypeOf` answers whenever `projectIdOf` does.
        const appType = appTypeOf(entity);
        return projectId && appType
          ? [
              {
                entityRef: stringifyEntityRef(entity),
                projectId,
                label: entity.metadata.title ?? entity.metadata.name,
                params: entityRouteParams(entity),
                appType,
                creation: projectCreation(entity),
                repositoryUrl: projectRepositoryUrl(entity),
              },
            ]
          : [];
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, options);
}

/**
 * Calls `listener` with every projects list read from the catalog from now on
 * (`SharedLoad.subscribe`): the sidebar and the home page follow each other's
 * reads, and the re-read on returning to the tab. Returns the unsubscribe.
 */
export function onProjectEntries(
  listener: (projects: ProjectEntry[]) => void,
): () => void {
  return sharedProjects.subscribe(listener);
}

/** The projects already read again for {@link ensureProjectListed}. */
const projectsReadAgainFor = new Set<string>();

/**
 * Makes sure the shared list knows project `projectId`, whose page is open —
 * a project created a moment ago, after the list was read. The page's entity
 * exists in the catalog, so a list read now has it. Nothing is asked when the
 * last list read has the project (most of the time), nor when no list was
 * read yet (the sidebar's first read will have it); otherwise ONE forced
 * read, whose subscribers ({@link onProjectEntries}) — the sidebar, the home
 * page — then show it. At most once per project and page load: a project the
 * list's filters leave out (`projectEntity.ts`) must not cost a request on
 * every visit. Rejects when the catalog does.
 */
export async function ensureProjectListed(
  catalogApi: Pick<CatalogApi, 'getEntities'>,
  projectId: string,
): Promise<void> {
  const listed = sharedProjects.latest();
  if (
    !listed ||
    listed.some(project => project.projectId === projectId) ||
    projectsReadAgainFor.has(projectId)
  ) {
    return;
  }
  projectsReadAgainFor.add(projectId);
  await loadProjectEntries(catalogApi, { force: true });
}

/**
 * Project `projectId`'s entry in the shared list: the branch page's way back
 * to its project (the « ‹ projet » button). The list the sidebar already
 * read, at no request of its own most of the time; for a project the list
 * lacks — created since —, {@link ensureProjectListed} reads it once
 * more. Of two entities carrying the same project, the first by entity ref,
 * so the button leads to the same place on every visit. Rejects when the
 * catalog does.
 */
export async function findProjectEntry(
  catalogApi: Pick<CatalogApi, 'getEntities'>,
  projectId: string,
): Promise<ProjectEntry | undefined> {
  const pick = (projects: ProjectEntry[]) =>
    projects
      .filter(project => project.projectId === projectId)
      .sort((a, b) => a.entityRef.localeCompare(b.entityRef))[0];
  const listed = pick(await loadProjectEntries(catalogApi));
  if (listed) {
    return listed;
  }
  await ensureProjectListed(catalogApi, projectId);
  return pick(await loadProjectEntries(catalogApi));
}

/**
 * Back to a fresh page load: the next {@link loadProjectEntries} asks the
 * catalog again, no list is known (`latest`), and
 * {@link ensureProjectListed} may read again for any project.
 */
export function forgetProjectEntries(): void {
  sharedProjects.clear();
  projectsReadAgainFor.clear();
}
